import { AnalyticsController } from './analytics-controller';
import {
  GTM_CONN_REQ_STEP1_VIEW,
  GTM_CONN_REQ_STEP1_SUBMIT,
  GTM_CONN_REQ_LEGAL_VIEW,
  GTM_CONN_REQ_SUCCESS_VIEW,
  GTM_CONN_REQ_DROPOFF,
  GTM_CONN_REQ_SUBMISSION,
  GTM_CONN_REQ_SUBMIT_MOBILE,
  GTM_CONN_REQ_SUBMIT_DESKTOP,
  GTM_PROHIBITED_EMAILS,
  GTM_CLICK_ADDRESS_PAGE_MAIN_PVR_CTA,
  GTM_XP_CONNECTION_REQUEST_FORM_CONTROL_CTA_CLICK,
  GTM_XP_CONNECTION_REQUEST_FORM_VARIATION_CTA_CLICK,
} from 'constants/events';
import type { ConnectionRequestSource } from 'domain/connection-request-form/types';

export type ConnectionRequestStep = 'contact' | 'legal' | 'loading' | 'success' | 'error';

/**
 * ConnectionRequestAnalytics
 *
 * Central handler for all analytics events fired by the connection request form.
 * The hook subscribes via two entry points:
 *
 * - `onStepTransition(prev, next)` — called on every step change; decides which
 *   funnel view events fire based on the transition.
 * - `onUnmount(currentStep, hasLegalData, submitted)` — called on form close/unmount;
 *   fires drop-off with the step label.
 * - `onContactSubmit()` — called when the user submits the contact step.
 * - `onSubmitSuccess(source, email)` — called on successful API response; fires
 *   all legacy compatibility events.
 *
 * ## Funnel events
 * | Event                  | Label        | Fires when                                      |
 * |------------------------|--------------|-------------------------------------------------|
 * | `XpConnReqStep1View`   | —            | transition: any → contact (initial mount)       |
 * | `XpConnReqStep1Submit` | —            | user submits the contact step                   |
 * | `XpConnReqLegalView`   | —            | transition: contact → legal                     |
 * | `XpConnReqSuccessView` | —            | transition: loading → success                   |
 * | `XpConnReqDropOff`     | step name    | unmount on any step without completing         |
 *
 * ## Legacy compatibility events (fire on submit success)
 * `property_viewing_request` / `property_info_request`, `connection-request-submission`,
 * `mobile-submit-pvr` or `desktop-submit-pvr` (viewport-dependent), `trackConnectionRequest()`
 */
export const ConnectionRequestAnalytics = {
  /**
   * Subscriber called on every step transition.
   * `prev` is `null` on the initial mount.
   */
  onStepTransition(prev: ConnectionRequestStep | null, next: ConnectionRequestStep): void {
    if (prev === null && next === 'contact') {
      AnalyticsController.trackEvent(GTM_CONN_REQ_STEP1_VIEW);
      return;
    }
    if (prev === 'contact' && next === 'legal') {
      AnalyticsController.trackEvent(GTM_CONN_REQ_LEGAL_VIEW);
      return;
    }
    if (prev === 'loading' && next === 'success') {
      AnalyticsController.trackEvent(GTM_CONN_REQ_SUCCESS_VIEW);
      return;
    }
  },

  /**
   * Subscriber called when the form unmounts (modal closed / navigated away).
   *
   * Fires `UiConnReqDropOff` with `label` set to the logical step the user was
   * on (`'contact'` or `'legal'`). Intermediate states (`loading` / `error`)
   * are resolved back to their logical step.
   */
  onUnmount(currentStep: ConnectionRequestStep, hasLegalData: boolean, submitted: boolean): void {
    if (submitted) return;

    let label: string = currentStep;
    if (currentStep === 'loading' || currentStep === 'error') {
      label = hasLegalData ? 'legal' : 'contact';
    }

    AnalyticsController.trackEvent(GTM_CONN_REQ_DROPOFF, 'All', label);
  },

  /**
   * User clicked a CTA button on the listing page to open the connection request form.
   * Fires the standard CTA click event plus the experiment-namespaced variant so GTM
   * can capture experiment params alongside the interaction.
   *
   * `variationId` — 1 = variation, anything else = control.
   */
  onCtaClick(buttonText: string, variationId: number | undefined): void {
    const experimentEvent = variationId === 1
      ? GTM_XP_CONNECTION_REQUEST_FORM_VARIATION_CTA_CLICK
      : GTM_XP_CONNECTION_REQUEST_FORM_CONTROL_CTA_CLICK;
    AnalyticsController.trackEvent(experimentEvent, 'All', buttonText);
    AnalyticsController.trackEvent(GTM_CLICK_ADDRESS_PAGE_MAIN_PVR_CTA, 'All', buttonText);
  },

  /** User submitted the contact step (advances to legal or submits directly). */
  onContactSubmit(): void {
    AnalyticsController.trackEvent(GTM_CONN_REQ_STEP1_SUBMIT);
  },

  /**
   * Fires all legacy events on a successful submission.
   * Mirrors the old form's `trackAllEvents` + `trackConnectionRequest`.
   * Skips prohibited test-automation emails.
   *
   * `isMobile` controls the viewport-dependent pvr event, matching the legacy
   * component's `isInModal ? 'mobile-submit-pvr' : 'desktop-submit-pvr'` branch.
   */
  onSubmitSuccess(source: ConnectionRequestSource, email: string, isMobile: boolean): void {
    if (GTM_PROHIBITED_EMAILS.includes(email)) return;

    const legacyEventAction = `property_${source === 'book-tour' ? 'viewing' : 'info'}_request`;
    AnalyticsController.trackEvent(legacyEventAction);
    AnalyticsController.trackEvent(GTM_CONN_REQ_SUBMISSION);
    AnalyticsController.trackEvent(isMobile ? GTM_CONN_REQ_SUBMIT_MOBILE : GTM_CONN_REQ_SUBMIT_DESKTOP);
    AnalyticsController.trackConnectionRequest();
  },
} as const;
